import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReusableModal from './ReusableModal';

const DataSelectionModal = ({
    show,
    onHide,
    title,
    DataComponent, // The component to render (e.g., FinishedItems, Customers, etc.)
    componentProps = {}, // Props to pass to the DataComponent
    
    // Selection mode: 'single' for row click, 'multiple' for row selection
    selectionMode = 'single',
    
    // Callbacks
    onDataSelected, // Called when data is selected (single mode)
    onRowsSelected, // Called when rows are selected (multiple mode)
    
    // Multiple selection specific props
    selectionButtonText = "Add Selected",
    selectionButtonIcon = "fa fa-plus",
    /** When true (default), modal closes after Add. Set false to keep open for multiple batches. */
    closeOnAdd = true,
    
    // Other modal props
    size = "xl",
    excludedActions = ['create', 'actions', 'filter']
}) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const lastSelectionRef = useRef(null);
    const isUpdatingRef = useRef(false);

    // Handle row click for single selection mode
    const handleRowClick = useCallback((data) => {
        if (selectionMode === 'single' && onDataSelected) {
            const shouldClose = onDataSelected(data);
            if (closeOnAdd && shouldClose !== false) {
                onHide();
            }
        }
    }, [selectionMode, onDataSelected, onHide, closeOnAdd]);

    // Handle row selection for multiple selection mode
    const handleRowSelection = useCallback((data) => {
        if (selectionMode === 'multiple' && !isUpdatingRef.current) {
            const rows = data?.selectedRows || [];
            const rowsString = JSON.stringify(rows);
            
            if (rowsString !== lastSelectionRef.current) {
                lastSelectionRef.current = rowsString;
                isUpdatingRef.current = true;
                setSelectedRows(rows);
                
                // Use setTimeout to prevent circular updates
                setTimeout(() => {
                    isUpdatingRef.current = false;
                }, 0);
            }
        }
    }, [selectionMode]);

    // Sync selectedIds with selectedRows
    useEffect(() => {
        if (selectionMode === 'multiple' && !isUpdatingRef.current) {
            const newSelectedIds = selectedRows.map(row => row.id);
            setSelectedIds(newSelectedIds);
        }
    }, [selectedRows, selectionMode]);

    const clearSelection = useCallback(() => {
        setSelectedRows([]);
        setSelectedIds([]);
        lastSelectionRef.current = null;
        isUpdatingRef.current = false;
    }, []);

    // Handle adding selected rows
    const handleAddSelected = useCallback(() => {
        let shouldClose = closeOnAdd;
        if (onRowsSelected) {
            const result = onRowsSelected(selectedRows);
            if (result === false) {
                shouldClose = false;
            }
        }
        if (shouldClose) {
            onHide();
        } else {
            clearSelection();
        }
    }, [selectedRows, onRowsSelected, onHide, closeOnAdd, clearSelection]);

    // Reset selection when modal is closed
    useEffect(() => {
        if (!show) {
            clearSelection();
        }
    }, [show, clearSelection]);

    // Prepare component props
    const dataComponentProps = {
        ...componentProps,
        excludedActions,
        ...(selectionMode === 'single' && { onClickedRow: handleRowClick }),
        ...(selectionMode === 'multiple' && { 
            onSelectedRowsChange: handleRowSelection,
            selectedIds 
        })
    };

    // Prepare modal actions
    const actions = selectionMode === 'multiple' ? [
        {
            text: selectionButtonText,
            icon: selectionButtonIcon,
            className: 'btn-primary',
            onClick: handleAddSelected,
            disabled: selectedRows.length === 0
        }
    ] : [];

    return (
        <ReusableModal
            show={show}
            onHide={onHide}
            title={title}
            size={size}
            actions={actions}
        >
            <DataComponent {...dataComponentProps} />
        </ReusableModal>
    );
};

export default DataSelectionModal; 